import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { Amount } from '@fcom/dapi/interfaces';

import { ButtonMode, ButtonTheme } from './../../buttons/button/enums';
import { ProductCardVariant } from './enums';
import { IconPosition } from '../../icons';
import { ResponsiveImage } from '../../images/interfaces';
import { TagTheme } from '../../tag';
import { NotificationLayout, NotificationTheme } from '../../notifications';
import { ProductTag } from '../../../../common-booking/modules/ancillaries/interfaces';

const stopPropagation = (event: Event): boolean => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  return false;
};

@Component({
  selector: 'fcom-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductCardComponent {
  readonly TagTheme = TagTheme;
  readonly ButtonMode = ButtonMode;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationLayout = NotificationLayout;

  hasAddedItems = input<boolean>();

  /**
   * The title of the product.
   */
  title = input<string>();

  /**
   * The detailed description of the product.
   */
  detailText = input<string>();

  /**
   * Control description visibility.
   */
  showDetailText = input(false);

  /**
   * The image of the product.
   */
  image = input<ResponsiveImage>();

  /**
   * The current status text to be shown for the product.
   * Should be used to to show for example how many of this product we have selected
   */
  statusText = input<string>();

  /**
   * The purchased text to be shown for the product.
   * Should be used to to show how many of this product have previously been bought
   */
  purchasedText = input<string>();

  /**
   * Price of the product
   */
  price = input<string>();

  /**
   * Text to be shown for view button
   */
  viewSummaryText = input<string>();

  /**
   * Primary button loading state for the product
   */
  loading = input<boolean>();

  /**
   * Primary button theme
   */
  buttonTheme = input<ButtonTheme>(ButtonTheme.TERTIARY_GHOST);

  /**
   * Primary button text
   */
  buttonText = input<string>();

  /**
   * Primary button aria text
   */
  buttonAriaText = input<string>();

  /**
   * If button should be disabled for the product
   */
  hideButton = input(false);

  /**
   * Primary button icon
   */
  buttonIcon = input<SvgLibraryIcon>(SvgLibraryIcon.PLUS);

  /**
   * Primary button icon position
   */
  buttonIconPosition = input<IconPosition>(IconPosition.RIGHT);

  /**
   * Notification translated text
   */
  notificationText = input<string>();

  /**
   * Notification translated title
   */
  notificationTitle = input<string>();

  /**
   * Notification icon
   */
  notificationIcon = input<SvgLibraryIcon>();

  /**
   * Notification theme
   */
  notificationTheme = input<NotificationTheme>(NotificationTheme.INFO);

  focusOnBtn = input(false);

  lowestPrice = input<string>();

  originalPrice = input<string>();

  productTags = input<ProductTag[]>([]);

  showStackableLabels = input(false);

  useNewProductCardLayout = input(false);

  variant = input<ProductCardVariant>(ProductCardVariant.PRIMARY);

  protected readonly isSecondaryVariant = computed(() => this.variant() === ProductCardVariant.SECONDARY);

  countText = input<string>();

  isLaptopUp = input<boolean>();

  aviosPrice = input<Amount | null>();

  aviosLowestPrice = input<string>();

  /**
   * Category icons to be shown in the product card. Only 1 or 2 icons are supported.
   * Should be used to show category specific icons, e.g. seat and wifi icons for wifi products
   * If one icon is provided, it will be shown as a single icon.
   * If two icons are provided, they will be joined by a plus icon.
   */
  categoryIcons = input<[SvgLibraryIcon] | [SvgLibraryIcon, SvgLibraryIcon]>();

  /**
   * Event emitter for selectProduct event from primary button
   */
  selectProduct = output<void>();

  /**
   * Event emitter for viewDetails event from view button
   */
  viewDetails = output<void>();

  emitProductCategory(event: Event): void {
    stopPropagation(event);
    this.selectProduct.emit();
  }

  emitViewDetails(event: Event): void {
    stopPropagation(event);
    this.viewDetails.emit();
  }
}
